/**
 * Kingdom Minded — anchor jump shim.
 *
 * WHY THIS EXISTS
 * Horizon locks the document: `html:has(.page-wrapper), html:has(.page-wrapper) body`
 * are set to `height: 100dvh; overflow: hidden` in base.css, and the page actually
 * scrolls inside `.page-wrapper`. Native fragment navigation scrolls the *document*,
 * which in this theme cannot scroll — so any link ending in `#some-id` lands at the top
 * of the target page instead of on the section. Verified directly: on /pages/shop-all
 * the element `#type-tees` sits 892px down, yet `scrollY` stayed 0 on arrival and the
 * page-wrapper's scrollTop never moved.
 *
 * WHAT IT DOES
 * On load and on hashchange, scrolls the real scroll container to the hash target,
 * honouring the target's `scroll-margin-top` so it clears the fixed header.
 *
 * Deliberately does NOT intercept clicks. A same-page anchor click updates the hash,
 * which fires `hashchange`, which this handles — so no `preventDefault`, no synthetic
 * history entries, and browser Back keeps behaving exactly as it does natively.
 */
(function () {
  function scrollerFor(el) {
    let node = el.parentElement;
    while (node && node !== document.body) {
      const overflowY = getComputedStyle(node).overflowY;
      if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
        return node;
      }
      node = node.parentElement;
    }
    return null;
  }

  function jump(hash, smooth) {
    if (!hash || hash.length < 2) return;

    let target = null;
    try {
      target = document.getElementById(decodeURIComponent(hash.slice(1)));
    } catch (e) {
      return;
    }
    if (!target) return;

    const box = scrollerFor(target);
    // If the document scrolls normally, leave the browser to do its own thing.
    if (!box) return;

    const margin = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
    const top =
      target.getBoundingClientRect().top -
      box.getBoundingClientRect().top +
      box.scrollTop -
      margin;

    /*
      Always "instant", never "auto". `.page-wrapper` carries `scroll-behavior: smooth`,
      and "auto" resolves to that CSS value - which here silently swallows the scroll:
      measured on /pages/shop-all, scrollTo({top:924,behavior:"auto"}) left scrollTop at 33
      even after 2s, while behavior:"instant" moved it immediately and held. The `smooth`
      argument is kept in the signature for callers but deliberately not honoured.
    */
    box.scrollTo({ top: Math.max(0, top), behavior: 'instant' });
  }

  function jumpOnArrival() {
    if (!location.hash) return;

    /*
      Chrome restores the scroll position it remembers for a URL it has already seen, and
      that restoration lands AFTER this shim runs - measured on /pages/shop-all#type-hoodies,
      the wrapper came back at a stale 1500px with the target 343px above the viewport.
      Suppress restoration for this arrival only, then hand control straight back so
      Back/Forward keep restoring scroll normally.
    */
    var priorRestoration = null;
    if ('scrollRestoration' in history) {
      priorRestoration = history.scrollRestoration;
      history.scrollRestoration = 'manual';
    }

    /*
      Re-run a few times. Lazy images and late webfonts above the target shift layout
      after first paint, which would otherwise leave the jump a few hundred pixels short.
    */
    jump(location.hash, false);
    [120, 350, 700, 1200].forEach(function (delay) {
      setTimeout(function () { jump(location.hash, false); }, delay);
    });

    setTimeout(function () {
      if (priorRestoration !== null) history.scrollRestoration = priorRestoration;
    }, 1400);
  }

  window.addEventListener('hashchange', function () { jump(location.hash, true); });
  window.addEventListener('popstate', function () { jump(location.hash, false); });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', jumpOnArrival);
  } else {
    jumpOnArrival();
  }
  window.addEventListener('load', function () { jump(location.hash, false); });
})();
